import firebase from "firebase/app";
import env from "../../server.config";
import "firebase/auth";

/**
 * @constructs AuthProvider
 * @description Instantiates a provider for Firebase authentication
 */
export default class AuthProvider {
  /**
   * @field lastErr
   * @description public error state of AuthProvider class
   */
  lastErr = "";

  constructor(syncFunction) {
    // initialize firebase session
    firebase.initializeApp(env.firebaseConfig);
    firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    firebase.auth().onAuthStateChanged((user) => {
      console.log("onAuthStateChanged ", user?.email);
      setTimeout(() => {
        // DER 012723 - I'm sure there is a better way to do this, but auth.GetCurrentUser() takes a bit to catch up to the state change event
        syncFunction();
      }, 500);
    });
  }

  fbGoogleLogin(api, callback) {
    const provider = new firebase.auth.GoogleAuthProvider();
    firebase
      .auth()
      .signInWithPopup(provider)
      .then(() => {
        // Now login to Qx Portal API
        const firebaseUser = firebase.auth().currentUser;
        if (firebaseUser) {
          console.log(
            "AuthProvider::fbGoogleLogin() firebase.auth().currentUser=" +
              firebaseUser,
          );
          let apiUser;
          firebaseUser
            .getIdToken(true)
            .then(async (idToken) => {
              // if idToken is returned, pass it to API to verify email against Hubspot users
              apiUser = await this.qxApiLogin(api, idToken);
              console.log("AuthProvider::fbGoogleLogin() apiUser:", apiUser);
            })
            .catch((err) => {
              console.log(
                "AuthProvider::fbGoogleLogin() getIdToken error=",
                err.message,
              );
              // Handle getIdToken error
              this.lastErr += `Failed to retrieve IdToken ${err.message}`;
              firebase
                .auth()
                .signOut()
                .catch((err) => {
                  this.lastErr += err.message;
                });
              apiUser = {};
            })
            .finally(() => {
              callback(apiUser);
            });
        }
      })
      .catch((error) => {
        // Handle Errors here.
        var errorCode = error.code;
        var errorMessage = error.message;
        var email = error.email;
        this.lastErr = `Error signing in with Google ${email} ${errorMessage} ${errorCode}`;
      });
  }

  /**
   * @method Login
   * @description signs a user into firebase
   * @param {Object} user requires email and password keys
   * @param {Object} api axios rest api
   * @param {Function} callback function that passes new userprofile as a parameter
   */
  fbEmailLogin(user, api, callback) {
    this.lastErr = "";
    firebase
      .auth()
      .signInWithEmailAndPassword(user.email, user.password)
      .then(() => {
        const firebaseUser = firebase.auth().currentUser;
        if (firebaseUser) {
          console.log(
            "AuthProvider::fbEmailLogin() firebase.auth().currentUser=" +
              firebaseUser,
          );
          if (firebaseUser.emailVerified) {
            // Now login to Qx Portal API
            let apiUser;
            firebaseUser
              .getIdToken(true)
              .then(async (idToken) => {
                // if idToken is returned, pass it to API to verify email against Hubspot users
                apiUser = await this.qxApiLogin(api, idToken);
                console.log("AuthProvider::fbEmailLogin() apiUser:", apiUser);
              })
              .catch((err) => {
                console.log(
                  "AuthProvider::fbEmailLogin() getIdToken error=",
                  err.message,
                );
                // Handle getIdToken error
                this.lastErr += `Failed to retrieve IdToken ${err.message}`;
                firebase
                  .auth()
                  .signOut()
                  .catch((err) => {
                    this.lastErr += err.message;
                  });
                apiUser = {};
              })
              .finally(() => {
                callback(apiUser);
              });
          } else {
            this.lastErr = "Email not yet verified";
            firebase
              .auth()
              .signOut()
              .catch((err) => {
                this.lastErr += err.message;
              });
            callback({});
          }
        }
      })
      .catch((err) => {
        this.lastErr = err.message;
        firebase
          .auth()
          .signOut()
          .catch((err) => {
            this.lastErr += err.message;
          });
        callback({});
      });
  }

  qxApiReLogin = async (api) => {
    let apiUser = {};
    try {
      const idToken = await firebase.auth().currentUser?.getIdToken(true);
      apiUser = await this.qxApiLogin(api, idToken);
    } catch(err) {
      console.log("AuthProvider::qxApiReLogin() getIdToken error=", err.message );
      // Handle getIdToken error
      this.lastErr += `Failed to retrieve IdToken ${err.message}`;
      firebase
        .auth()
        .signOut()
        .catch((err) => {
          this.lastErr += err.message;
        });
      apiUser = {};
    }
    return apiUser;
  }

  qxApiLogin = async (api, idToken) => {
    let user = {};
    try {
      const response = await api({
        method: "post",
        url: `/API/public/login?token=${idToken}`,
      });
      user = response.data;
      user.visibleCompanies.sort((a, b) => (a.name > b.name ? 1 : -1));
    } catch (err) {
      //handle axios API login error - I get a 403 unauthorized if this email doesn't pass Hubspot tests
      user = {};
      this.lastErr += `User not authorized for partner portal access. ${err.message}`;
      console.error("AuthProvider::Login(): " + this.lastErr);
      firebase
        .auth()
        .signOut()
        .catch((err) => {
          this.lastErr += err.message;
        });
    }
    return user;
  };

  /**
   * @method SignOut
   * @description signs a user out of firebase
   * @param {Object} user requires token key
   * @param {Object} api axios rest api
   * @param {Function} reset function from top level app
   */
  SignOut(user, api, reset) {
    firebase
      .auth()
      .signOut()
      .then(() => {
        // long timeout because login takes a while to map reps to integrators
        api({
          method: "post",
          url: "/API/public/logout",
          timeout: 30000,
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        })
          .then(() => {
            console.log("AuthProvider::SignOut() API logout");
          })
          .catch((err) => {
            //A portal API error happened
            console.error("AuthProvider::SignOut() portal error:", err);
          });
      })
      .catch((err) => {
        // A Firebase error happened.
        console.error("AuthProvider::SignOut() firebase error: ", err);
      })
      .finally(() => {
        console.log("AuthProvider::SignOut() user logged out");
        reset();
      });
  }

  /**
   * @method CreateAccount
   * @description creates a new account with specified email and password
   * @param {Object} user requires email and password keys
   * @param {Function} callback function that passes success boolean
   */
  CreateAccount(user, callback) {
    this.lastErr = "";
    firebase
      .auth()
      .createUserWithEmailAndPassword(user.email, user.password)
      .then(() => {
        firebase
          .auth()
          .currentUser.sendEmailVerification()
          .then(() => {
            this.lastErr =
              "Login again after responding to the email verification message";
            callback(true);
          })
          .catch((err) => {
            this.lastErr += err.message;
            callback(false);
          });
      })
      .catch((err) => {
        this.lastErr = `${err.message} ${err.code}`;
        callback(false);
      });
    firebase
      .auth()
      .signOut()
      .catch((err) => {
        this.lastErr += err.message;
        callback(false);
      });
    user.email = "";
    user.password = "";
    console.log(this.lastErr);
  }

  /**
   * @method PasswordReset
   * @description sends a password reset email to the user in question
   * @param {Object} user requires email key
   * @param {Function} callback function that passes success boolean
   */
  PasswordReset(user, callback) {
    this.lastErr = "";
    if (user.email) {
      if (confirm(`Do you want to reset the password for ${user.email}`)) {
        console.log(
          `login::passwordReset() resetting firebase password for ${user.email}`,
        );
        firebase
          .auth()
          .sendPasswordResetEmail(user.email)
          .then(() => {
            this.lastErr = "Password reset email sent";
            callback(true);
          })
          .catch((err) => {
            console.log(`login::passwordReset()  error: ${err}`);
            this.lastErr = err;
            callback(false);
          });
      }
    } else {
      this.lastErr = "Enter a valid email for password reset";
      callback(false);
    }
    return;
  }

  /**
   * @method UserIsSignedIn
   * @returns boolean indicating whether or not a user is signed in this session
   * @description
   * determines whether or not a user is signed in
   * using localStorage may be a better implementation:
   * {@link https://stackoverflow.com/a/51856627}
   * @example
   * if(AuthProvider.UserIsSignedIn()) {
   *  // do something
   * }
   */
  UserIsSignedIn() {
    const storage = localStorage.getItem("userProfile");
    if (storage) {
      const user = JSON.parse(storage);
      console.log(
        "AuthProvider::UserIsSignedIn() user length:",
        Object.keys(user).length,
      );
      if (Object.keys(user).length > 0) {
        // a user is signed in.
        return true;
      } else {
        // no user signed in
        return false;
      }
    } else return false;
  }

  /**
   * @method GetCurrentUser
   * @description wrapper to the current user from firebase
   * @return current user
   */
  GetCurrentUser() {
    const storage = localStorage.getItem("userProfile");
    let user;
    if (storage) {
      try {
        user = JSON.parse(storage);
      } catch (err) {
        console.error("auth::GetCurrentUser() json parse error");
        localStorage.clear();
      }
      return user;
    } else {
      return {};
    }
  }
}
